import styles from "./Player.module.css";
import { FaSignal } from "react-icons/fa";
import PlayerAvatar from "./PlayerAvatar";
import useTranslation from "next-translate/useTranslation";
import { useEffect, useState } from "react";
import { Color, PieceSymbol as ChessPieceSymbol } from "@chesshotel/chess";
import { PieceSymbol } from "@/util/chessNotation";

const CAPTURED_PIECE_ORDER: ChessPieceSymbol[] = ["q", "r", "b", "n", "p"];

function CapturedPieces({
  capturedPieces = [],
  materialAdvantage = 0,
}: {
  capturedPieces?: { piece: ChessPieceSymbol; color: Color }[];
  materialAdvantage?: number;
}) {
  if (!capturedPieces.length && !materialAdvantage) return null;

  const sortedPieces = [...capturedPieces].sort(
    (a, b) =>
      CAPTURED_PIECE_ORDER.indexOf(a.piece) -
      CAPTURED_PIECE_ORDER.indexOf(b.piece),
  );

  return (
    <div className="flex flex-wrap gap-1 items-center ml-2 min-w-0 max-w-[7.25rem] leading-none md:max-w-[8.75rem]">
      {sortedPieces.map((captured, index) => (
        <span
          key={`${captured.color}-${captured.piece}-${index}`}
          className="inline-flex justify-center items-center w-[1em] h-[1em] text-[13px] opacity-85 md:text-[15px]"
          aria-hidden="true"
        >
          <PieceSymbol
            piece={captured.piece}
            color={captured.color}
            className="m-0"
            gapRight={false}
          />
        </span>
      ))}
      {materialAdvantage > 0 && (
        <span className="ml-1 text-xs font-semibold text-[#296ea3]">
          +{materialAdvantage}
        </span>
      )}
    </div>
  );
}

function Player({
  elo,
  previousElo,
  displayName,
  gameEnded,
  avatarId,
  fbId,
  googleId,
  isUser,
  isGuest,
  id,
  avatarType,
  children,
  disconnected = false,
  disconnectTimeout = null,
  disconnectTimeoutStart = null,
  capturedPieces = [],
  materialAdvantage = 0,
}) {
  const { t } = useTranslation("common");
  const [remainingTime, setRemainingTime] = useState(0);

  useEffect(() => {
    if (!disconnected || !disconnectTimeout || !disconnectTimeoutStart) {
      setRemainingTime(0);
      return;
    }

    const updateTimer = () => {
      const elapsed = Date.now() - disconnectTimeoutStart;
      const remaining = Math.max(
        0,
        Math.ceil((disconnectTimeout - elapsed) / 1000),
      );
      setRemainingTime(remaining);
    };

    // Update immediately
    updateTimer();

    // Then update every second
    const intervalId = setInterval(updateTimer, 1000);

    // Cleanup interval on unmount or when disconnected state changes
    return () => clearInterval(intervalId);
  }, [disconnected, disconnectTimeout, disconnectTimeoutStart]);

  function renderElo() {
    if (!elo) {
      return;
    }
    if (previousElo) {
      const diff = elo - previousElo;
      let diffTag;

      if (diff > 0) {
        diffTag = <span className={styles.diffEqual}>+{diff}</span>;
      } else if (diff < 0) {
        diffTag = <span className={styles.diffEqual}>{diff}</span>;
      } else {
        diffTag = <span className={styles.diffEqual}>0</span>;
      }

      return (
        <div className="inline-flex font-normal">
          <div className="pr-1 text-base">
            <FaSignal />
          </div>
          {elo} ({diffTag})
        </div>
      );
    } else {
      return (
        <div className="inline-flex font-normal">
          <div className="pr-1 text-base">
            <FaSignal />
          </div>
          {elo}
        </div>
      );
    }
  }

  const usernameStyle = gameEnded ? styles.usernameGameEnded : styles.username;

  return (
    <div className={styles.player}>
      <div className={`font-semibold bg-gray-200 ${styles.header}`}>
        <div className={usernameStyle}>{displayName}</div>
        {renderElo()}
      </div>
      <div className={styles.body}>
        <div
          className={
            disconnected
              ? "flex space-x-2 items-center min-w-0 animate-pulse"
              : "flex items-center min-w-0"
          }
        >
          <div className="flex items-center min-w-0">
            <PlayerAvatar
              {...{
                isUser,
                avatarId,
                fbId,
                googleId,
                isGuest,
                id,
                avatarType,
                gameEnded,
                status: disconnected ? "offline" : "neutral",
              }}
            />
            {/* <CapturedPieces
              capturedPieces={capturedPieces}
              materialAdvantage={materialAdvantage}
            /> */}
          </div>
          {disconnected && (
            <div className="text-xs italic">
              <span className="hidden md:inline">
                {t("Reconnecting")}
                <br />
              </span>
              {remainingTime}s
            </div>
          )}
        </div>

        {children}
      </div>
    </div>
  );
}

export default Player;
